import type { Dispatch, SetStateAction } from 'react'
import { logFrontendRuntimeEvent } from '@emporion/ui'
import type { AdminLoginForm, Translator } from './useAdminAuth.types'
import type { AdminLoginStage } from './useAdminAuth.shared'

export type AdminLoginPayload = Record<string, unknown>

const toLoginError = ({ message, code }: { message: string; code?: string }): Error => {
  const error = new Error(message) as Error & { code?: string }
  if (code) {
    error.code = code
  }
  return error
}

const resolveGatewayError = (
  payload: AdminLoginPayload | null,
): { code: string; message: string } => {
  const nested =
    payload && typeof payload.error === 'object' && payload.error !== null
      ? (payload.error as Record<string, unknown>)
      : null
  const nestedCode = typeof nested?.code === 'string' ? nested.code.trim() : ''
  const nestedMessage = typeof nested?.message === 'string' ? nested.message.trim() : ''

  const code = nestedCode || (typeof payload?.code === 'string' ? payload.code.trim() : '')
  const message =
    nestedMessage || (typeof payload?.message === 'string' ? payload.message.trim() : '')

  return { code, message }
}

export const parseAdminLoginPayload = (raw: string): AdminLoginPayload | null => {
  if (!raw) return null
  try {
    return JSON.parse(raw) as AdminLoginPayload
  } catch (parseError) {
    logFrontendRuntimeEvent({
      app: 'admin',
      level: 'warn',
      event: 'auth.login_response_parse_failed',
      message: 'Failed to parse login response',
      error: parseError,
    })
    return null
  }
}

export const handleAdminLoginFailure = ({
  responseStatus,
  payload,
  raw,
  t,
  retryAfterSeconds,
  setLoginStage,
  setLoginForm,
}: {
  responseStatus: number
  payload: AdminLoginPayload | null
  raw: string
  t: Translator
  retryAfterSeconds?: number | null
  setLoginStage: Dispatch<SetStateAction<AdminLoginStage>>
  setLoginForm: Dispatch<SetStateAction<AdminLoginForm>>
}): { authenticated: false; otpRequired: true } => {
  // Auth API can return either explicit OTP codes or localized free-text messages.
  const gatewayError = resolveGatewayError(payload)
  const apiCode = gatewayError.code
  const apiMessage = gatewayError.message || raw || ''

  const otpRequired =
    apiCode === 'OTP_REQUIRED' ||
    apiCode === 'SECOND_FACTOR_REQUIRED' ||
    /otp richiesto|otp required|recovery code richiesto|recovery code required/i.test(apiMessage)
  if (otpRequired) {
    const sessionId = typeof payload?.sessionId === 'string' ? payload.sessionId.trim() : ''
    setLoginStage('otp')
    setLoginForm((prev) => ({ ...prev, otp: '', botProtectionToken: '', twoFactorSessionId: sessionId }))
    return { authenticated: false, otpRequired: true }
  }

  if (apiCode === 'AUTH_REQUIRED') {
    throw toLoginError({
      message: apiMessage || 'AUTH_REQUIRED',
      code: 'AUTH_REQUIRED',
    })
  }

  if (responseStatus === 429 || apiCode === 'RATE_LIMITED') {
    const fallbackMessage =
      retryAfterSeconds && retryAfterSeconds > 0
        ? `Troppi tentativi di accesso. Riprova tra ${retryAfterSeconds} secondi.`
        : 'Troppi tentativi di accesso. Riprova più tardi.'
    throw toLoginError({
      message: apiMessage || fallbackMessage,
      code: 'RATE_LIMITED',
    })
  }

  const message = apiMessage || (responseStatus === 409 ? t('auth.error.setupIncomplete') : t('auth.error.invalid'))
  throw toLoginError({
    message,
    code: apiCode || undefined,
  })
}
